import React from "react";
import { Task, Status } from "../utils/taskGenerator";
import { useTasks } from "../store/useTasks";
import { UserPresence, simulatePresence } from "../utils/collaborationSimulator";

interface Props {
  tasks: Task[];
}

const statuses: Status[] = ["To Do", "In Progress", "In Review", "Done"];

const KanbanView: React.FC<Props> = ({ tasks }) => {
  const [draggingTaskId, setDraggingTaskId] = React.useState<string | null>(null);
  const [presence, setPresence] = React.useState<UserPresence[]>([]);

  // Simulate live collaboration
  React.useEffect(() => {
    const taskIds = tasks.map((t) => t.id);
    let users = simulatePresence(taskIds);
    setPresence(users);

    const interval = setInterval(() => {
      users = users.map((u) => ({
        ...u,
        taskId: taskIds[Math.floor(Math.random() * taskIds.length)],
      }));
      setPresence([...users]);
    }, 3000);

    return () => clearInterval(interval);
  }, [tasks]);

  return (
    <div>
      {/* Top bar showing active users */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {presence.map((p) => (
          <span
            key={p.id}
            className={`w-6 h-6 rounded-full text-xs flex items-center justify-center text-white ${p.color}`}
          >
            {p.name}
          </span>
        ))}
        <span className="text-sm text-gray-600">
          {presence.length} people are viewing this board
        </span>
      </div>

      {/* Kanban grid - responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statuses.map((status) => {
          const columnTasks = tasks.filter((t) => t.status === status);

          return (
            <div
              key={status}
              data-status={status}
              className={`bg-gray-50 rounded shadow flex flex-col max-h-[80vh] transition-colors ${
                draggingTaskId ? "hover:bg-blue-50" : ""
              }`}
            >
              {/* Column Header */}
              <div className="p-2 font-semibold bg-gray-200 flex justify-between">
                <span>{status}</span>
                <span>{columnTasks.length}</span>
              </div>

              {/* Task List */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {columnTasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-24 text-gray-500 italic">
                    <span>No tasks in {status}</span>
                    <span className="text-xs">Try adjusting filters or adding tasks</span>
                  </div>
                ) : (
                  columnTasks.map((task) =>
                    draggingTaskId === task.id ? (
                      // Placeholder while dragging
                      <div
                        key={task.id}
                        className="bg-gray-200 rounded shadow h-16 animate-pulse"
                      />
                    ) : (
                      <KanbanCard
                        key={task.id}
                        task={task}
                        setDraggingTaskId={setDraggingTaskId}
                        presence={presence}
                      />
                    )
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KanbanView;

interface CardProps {
  task: Task;
  setDraggingTaskId: (id: string | null) => void;
  presence: UserPresence[];
}

const KanbanCard: React.FC<CardProps> = ({ task, setDraggingTaskId, presence }) => {
  const { updateTask } = useTasks();

  const today = new Date().toISOString().split("T")[0];
  const overdueDays =
    new Date(task.dueDate).getTime() < new Date(today).getTime()
      ? Math.floor(
          (new Date(today).getTime() - new Date(task.dueDate).getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : 0;

  const priorityColors: Record<string, string> = {
    Critical: "bg-red-600",
    High: "bg-orange-500",
    Medium: "bg-yellow-400",
    Low: "bg-green-500",
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    setDraggingTaskId(task.id);

    const card = e.currentTarget as HTMLElement;
    card.setPointerCapture(e.pointerId);

    // Ghost card
    const ghost = card.cloneNode(true) as HTMLElement;
    ghost.style.position = "fixed";
    ghost.style.pointerEvents = "none";
    ghost.style.opacity = "0.8";
    ghost.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
    ghost.style.width = `${card.offsetWidth}px`;
    document.body.appendChild(ghost);

    const move = (ev: PointerEvent) => {
      ghost.style.left = ev.clientX - card.offsetWidth / 2 + "px";
      ghost.style.top = ev.clientY - card.offsetHeight / 2 + "px";
    };

    const up = (ev: PointerEvent) => {
      setDraggingTaskId(null);
      card.releasePointerCapture(e.pointerId);

      // Snap-back animation
      ghost.style.transition = "all 0.3s ease";
      ghost.style.left = card.getBoundingClientRect().left + "px";
      ghost.style.top = card.getBoundingClientRect().top + "px";

      setTimeout(() => {
        if (document.body.contains(ghost)) {
          document.body.removeChild(ghost);
        }
      }, 300);

      // Drop zone detection
      const target = document.elementFromPoint(ev.clientX, ev.clientY);
      if (target?.closest("[data-status]")) {
        const newStatus = target.closest("[data-status]")?.getAttribute("data-status");
        if (newStatus) {
          updateTask(task.id, { status: newStatus as any });
        }
      }

      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
    };

    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  };

  return (
    <div
      className="bg-white rounded shadow p-2 cursor-pointer"
      onPointerDown={handlePointerDown}
    >
      <div className="flex justify-between items-center">
        <span className="font-medium">{task.title}</span>
        <span
          className={`text-xs px-2 py-1 rounded text-white ${
            priorityColors[task.priority]
          }`}
        >
          {task.priority}
        </span>
      </div>
      <div className="flex justify-between items-center mt-1 text-sm">
        <span className="bg-gray-300 rounded-full px-2 py-1 text-xs">
          {task.assignee}
        </span>
        <span
          className={`text-xs ${
            overdueDays > 0
              ? "text-red-600 font-semibold"
              : task.dueDate === today
              ? "text-blue-600 font-semibold"
              : "text-gray-600"
          }`}
        >
          {task.dueDate === today
            ? "Due Today"
            : overdueDays > 7
            ? `${overdueDays} days overdue`
            : task.dueDate}
        </span>
      </div>

      {/* Collaboration avatars */}
      <div className="flex mt-2 space-x-1">
        {presence
          .filter((p) => p.taskId === task.id)
          .map((p) => (
            <span
              key={p.id}
              className={`w-5 h-5 rounded-full text-xs flex items-center justify-center text-white ${p.color}`}
            >
              {p.name}
            </span>
          ))}
      </div>
    </div>
  );
};
